// src/pages/DbSchema.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DbSchemaViewer from '../components/DbSchemaViewer';
import ArticleSchemaManager from '../components/ArticleSchemaManager';

const DbSchema = () => {
  const [dbTestResult, setDbTestResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [createResult, setCreateResult] = useState(null);
  const [indicesData, setIndicesData] = useState(null);
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const [connectionStats, setConnectionStats] = useState(null);
  const [quoteConnections, setQuoteConnections] = useState(null);
  const [selectedQuoteId, setSelectedQuoteId] = useState('');

  // Fetch database test data
  const fetchTestData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/db-test');
      if (!response.ok) {
        throw new Error(`Failed to fetch test data: ${response.statusText}`);
      }
      const data = await response.json();
      setDbTestResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Create tables and add sample data
  const createTablesAndData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/db-test?action=create-tables');
      if (!response.ok) {
        throw new Error(`Failed to create tables: ${response.statusText}`);
      }
      const data = await response.json();
      setCreateResult(data);
      // Refresh test data
      fetchTestData();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Get indices data
  const getIndicesData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/db-test?action=get-indices');
      if (!response.ok) {
        throw new Error(`Failed to get indices data: ${response.statusText}`);
      }
      const data = await response.json();
      setIndicesData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Also test the direct indices endpoint
  const testIndicesEndpoint = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/admin/indices');
      if (!response.ok) {
        throw new Error(`Failed to get indices from endpoint: ${response.statusText}`);
      }
      const data = await response.json();
      console.log("Direct indices endpoint returned:", data);
      setIndicesData({
        directEndpoint: true,
        data: data
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Check quote connections
  const checkQuoteConnections = async (quoteId = null) => {
    setLoading(true);
    setError(null);
    try {
      const url = quoteId 
        ? `/api/db-test?action=check-connections&quoteId=${quoteId}`
        : '/api/db-test?action=check-connections';
        
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to check connections: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log("Connection check returned:", data);
      
      if (quoteId) {
        setQuoteConnections(data.tables?.quoteConnections || null);
        // Clear overall stats when looking at a specific quote
        setConnectionStats(null);
      } else {
        setConnectionStats(data.tables?.connectionCounts || null);
        // Clear specific quote data when looking at overall stats
        setQuoteConnections(null);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Fix connections for a specific quote
  const fixQuoteConnections = async (quoteId) => {
    if (!quoteId) {
      setError("Quote ID is required to fix connections");
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/db-test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'fix-connections',
          quoteId: quoteId
        })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fix connections: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log("Fix connections returned:", data);
      
      // Show success message
      setCreateResult({
        message: `Successfully fixed connections for quote ${quoteId}`,
        details: `Added: ${data.results.tactics_added} tactics, ${data.results.precedents_added} precedents, ${data.results.pillars_added} pillars`
      });
      
      // Refresh the connections data for this quote
      checkQuoteConnections(quoteId);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-4">
      <div className="mb-6">
        <Link to="/" className="text-blue-600 hover:text-blue-800 flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Home
        </Link>
      </div>
      
      <div className="mb-4">
        <button 
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          onClick={() => {
            setShowDiagnostics(!showDiagnostics);
            if (!dbTestResult && !showDiagnostics) {
              fetchTestData();
            }
          }}
        >
          {showDiagnostics ? 'Hide Diagnostics Tools' : 'Show Diagnostics Tools'}
        </button>
      </div>
      
      {showDiagnostics && (
        <div className="mb-8 bg-gray-50 p-4 rounded-md border border-gray-200">
          <h2 className="text-xl font-bold mb-4">Database Diagnostics Tools</h2>
          
          <div className="flex flex-wrap gap-2 mb-4">
            <button
              onClick={fetchTestData}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Check Database Tables'}
            </button>
            
            <button
              onClick={createTablesAndData}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Democracy Indices Tables'}
            </button>
            
            <button
              onClick={getIndicesData}
              className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Get Formatted Indices Data'}
            </button>
            
            <button
              onClick={testIndicesEndpoint}
              className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700"
              disabled={loading}
            >
              {loading ? 'Testing...' : 'Test /api/admin/indices Endpoint'}
            </button>
            
            <button
              onClick={() => checkQuoteConnections()}
              className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700"
              disabled={loading}
            >
              {loading ? 'Checking...' : 'Check Quote Connections'}
            </button>
          </div>
          
          {error && (
            <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
              <p className="font-bold">Error</p>
              <p>{error}</p>
            </div>
          )}
          
          {createResult && (
            <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-4">
              <p className="font-bold">Success</p>
              <p>Successfully created tables and added sample data</p>
            </div>
          )}
          
          {dbTestResult && (
            <div className="bg-white shadow-md rounded p-4 mb-4">
              <h3 className="text-lg font-semibold mb-2">Database Status</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {dbTestResult.tables?.democracyIndicesEnhanced && (
                  <div className="p-3 bg-blue-50 rounded">
                    <h4 className="font-medium">Democracy Indices Table</h4>
                    <p className="text-sm">
                      {dbTestResult.tables.democracyIndicesEnhanced.exists 
                        ? `✅ Exists (${dbTestResult.tables.democracyIndicesEnhanced.count} records)`
                        : '❌ Missing'}
                    </p>
                  </div>
                )}
                
                {dbTestResult.tables?.democracyIndexSources && (
                  <div className="p-3 bg-blue-50 rounded">
                    <h4 className="font-medium">Democracy Index Sources</h4>
                    <p className="text-sm">
                      {dbTestResult.tables.democracyIndexSources.exists 
                        ? `✅ Exists (${dbTestResult.tables.democracyIndexSources.count} sources)`
                        : '❌ Missing'}
                    </p>
                  </div>
                )}
              </div>
              
              <div className="mb-4">
                <h4 className="font-medium mb-2">All Tables</h4>
                <div className="flex flex-wrap gap-2">
                  {dbTestResult.tables?.allTables?.map(table => (
                    <span key={table} className="px-2 py-1 bg-gray-100 text-xs rounded">{table}</span>
                  ))}
                </div>
              </div>
              
              {dbTestResult.tables?.democracyIndicesEnhanced?.sampleData && (
                <div>
                  <h4 className="font-medium mb-2">Sample Data</h4>
                  <div className="overflow-x-auto">
                    <pre className="text-xs bg-gray-100 p-3 rounded max-h-40 overflow-auto">
                      {JSON.stringify(dbTestResult.tables.democracyIndicesEnhanced.sampleData, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {indicesData && (
            <div className="bg-white shadow-md rounded p-4 mb-4">
              <h3 className="text-lg font-semibold mb-2">
                {indicesData.directEndpoint 
                  ? 'Direct Indices Endpoint Test' 
                  : 'Formatted Indices Data'}
              </h3>
              <div className="overflow-x-auto">
                <pre className="text-xs bg-gray-100 p-3 rounded max-h-64 overflow-auto">
                  {JSON.stringify(indicesData.directEndpoint ? indicesData.data : indicesData.tables?.formattedIndices, null, 2)}
                </pre>
              </div>
            </div>
          )}
          
          {/* Quote Connections Section */}
          <div className="bg-white shadow-md rounded p-4 mb-4">
            <h3 className="text-lg font-semibold mb-2">Quote Connections Diagnostics</h3>
            
            {/* Search for quote or view all stats */}
            <div className="flex items-center mb-4 gap-2">
              <input
                type="text"
                value={selectedQuoteId}
                onChange={(e) => setSelectedQuoteId(e.target.value)}
                placeholder="Enter Quote ID"
                className="border rounded-md p-2 flex-grow"
              />
              <button
                onClick={() => selectedQuoteId ? checkQuoteConnections(selectedQuoteId) : checkQuoteConnections()}
                className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                disabled={loading}
              >
                {loading ? 'Checking...' : selectedQuoteId ? 'Check Quote' : 'Check All Quotes'}
              </button>
              
              {selectedQuoteId && (
                <button
                  onClick={() => fixQuoteConnections(selectedQuoteId)}
                  className="px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                  disabled={loading}
                >
                  {loading ? 'Fixing...' : 'Fix Connections'}
                </button>
              )}
            </div>
            
            {/* Display connection stats */}
            {connectionStats && (
              <div className="mb-4">
                <h4 className="font-medium mb-2">Connection Statistics</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                  <div className="border rounded-md p-3 bg-blue-50">
                    <div className="text-sm font-medium text-gray-600">Total Quotes</div>
                    <div className="text-2xl font-bold">{connectionStats.quotes}</div>
                  </div>
                  <div className="border rounded-md p-3 bg-green-50">
                    <div className="text-sm font-medium text-gray-600">Quotes with Tactics</div>
                    <div className="text-2xl font-bold">{connectionStats.quote_tactic_connections}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {connectionStats.quotes - connectionStats.quote_tactic_connections} missing
                    </div>
                  </div>
                  <div className="border rounded-md p-3 bg-yellow-50">
                    <div className="text-sm font-medium text-gray-600">Quotes with Precedents</div>
                    <div className="text-2xl font-bold">{connectionStats.quote_precedent_connections}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {connectionStats.quotes - connectionStats.quote_precedent_connections} missing
                    </div>
                  </div>
                  <div className="border rounded-md p-3 bg-purple-50">
                    <div className="text-sm font-medium text-gray-600">Quotes with Pillars</div>
                    <div className="text-2xl font-bold">{connectionStats.quote_pillar_impacts}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {connectionStats.quotes - connectionStats.quote_pillar_impacts} missing
                    </div>
                  </div>
                </div>
                
                {/* Display missing quotes (up to 10) */}
                {connectionStats.quotes_missing_tactics?.length > 0 && (
                  <div className="mb-3">
                    <h5 className="font-medium text-sm mb-1">Quotes Missing Tactics:</h5>
                    <div className="max-h-32 overflow-y-auto bg-gray-50 rounded p-2 text-sm">
                      {connectionStats.quotes_missing_tactics.map(quote => (
                        <div key={`tactic-${quote.id}`} className="mb-1 pb-1 border-b">
                          <span className="font-medium mr-2">Quote {quote.id}:</span>
                          <span className="text-gray-600">{quote.claim_text?.substring(0, 90)}...</span>
                          <button
                            onClick={() => {
                              setSelectedQuoteId(quote.id.toString());
                              checkQuoteConnections(quote.id);
                            }}
                            className="ml-2 px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded"
                          >
                            Inspect
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {connectionStats.quotes_missing_precedents?.length > 0 && (
                  <div className="mb-3">
                    <h5 className="font-medium text-sm mb-1">Quotes Missing Precedents:</h5>
                    <div className="max-h-32 overflow-y-auto bg-gray-50 rounded p-2 text-sm">
                      {connectionStats.quotes_missing_precedents.map(quote => (
                        <div key={`precedent-${quote.id}`} className="mb-1 pb-1 border-b">
                          <span className="font-medium mr-2">Quote {quote.id}:</span>
                          <span className="text-gray-600">{quote.claim_text?.substring(0, 90)}...</span>
                          <button
                            onClick={() => {
                              setSelectedQuoteId(quote.id.toString());
                              checkQuoteConnections(quote.id);
                            }}
                            className="ml-2 px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded"
                          >
                            Inspect
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {connectionStats.quotes_missing_pillars?.length > 0 && (
                  <div className="mb-3">
                    <h5 className="font-medium text-sm mb-1">Quotes Missing Pillars:</h5>
                    <div className="max-h-32 overflow-y-auto bg-gray-50 rounded p-2 text-sm">
                      {connectionStats.quotes_missing_pillars.map(quote => (
                        <div key={`pillar-${quote.id}`} className="mb-1 pb-1 border-b">
                          <span className="font-medium mr-2">Quote {quote.id}:</span>
                          <span className="text-gray-600">{quote.claim_text?.substring(0, 90)}...</span>
                          <button
                            onClick={() => {
                              setSelectedQuoteId(quote.id.toString());
                              checkQuoteConnections(quote.id);
                            }}
                            className="ml-2 px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded"
                          >
                            Inspect
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Display specific quote connections */}
            {quoteConnections && (
              <div className="mb-4">
                <h4 className="font-medium mb-2">Quote #{quoteConnections.quoteId} Connections</h4>
                
                {quoteConnections.exists ? (
                  <div>
                    {/* Quote details */}
                    <div className="bg-gray-50 p-3 rounded mb-3">
                      <div className="font-medium">Quote Text</div>
                      <div className="text-sm mt-1">{quoteConnections.quoteData.claim_text}</div>
                      {quoteConnections.quoteData.reality_text && (
                        <>
                          <div className="font-medium mt-2">Reality Check</div>
                          <div className="text-sm mt-1">{quoteConnections.quoteData.reality_text.substring(0, 200)}...</div>
                        </>
                      )}
                    </div>
                    
                    {/* Connection status */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                      <div className="border rounded p-3 bg-green-50">
                        <h5 className="font-medium">Authoritarian Tactics</h5>
                        {quoteConnections.tactics && quoteConnections.tactics.length > 0 ? (
                          <div className="text-green-800">
                            ✓ {quoteConnections.tactics.length} connection{quoteConnections.tactics.length !== 1 ? 's' : ''}
                          </div>
                        ) : (
                          <div className="text-red-800">✗ No tactics connected</div>
                        )}
                      </div>
                      
                      <div className="border rounded p-3 bg-yellow-50">
                        <h5 className="font-medium">Historical Precedents</h5>
                        {quoteConnections.precedents && quoteConnections.precedents.length > 0 ? (
                          <div className="text-green-800">
                            ✓ {quoteConnections.precedents.length} connection{quoteConnections.precedents.length !== 1 ? 's' : ''}
                          </div>
                        ) : (
                          <div className="text-red-800">✗ No precedents connected</div>
                        )}
                      </div>
                      
                      <div className="border rounded p-3 bg-purple-50">
                        <h5 className="font-medium">Democratic Pillars</h5>
                        {quoteConnections.pillars && quoteConnections.pillars.length > 0 ? (
                          <div className="text-green-800">
                            ✓ {quoteConnections.pillars.length} connection{quoteConnections.pillars.length !== 1 ? 's' : ''}
                          </div>
                        ) : (
                          <div className="text-red-800">✗ No pillars connected</div>
                        )}
                      </div>
                    </div>
                    
                    {/* Quick actions */}
                    <div className="flex gap-2 mb-3">
                      <button
                        onClick={() => fixQuoteConnections(quoteConnections.quoteId)}
                        className={`px-3 py-1.5 text-white rounded font-medium ${
                          (quoteConnections.tactics.length === 0 || 
                           quoteConnections.precedents.length === 0 || 
                           quoteConnections.pillars.length === 0) 
                            ? 'bg-green-600 hover:bg-green-700' 
                            : 'bg-gray-400'
                        }`}
                        disabled={
                          quoteConnections.tactics.length > 0 && 
                          quoteConnections.precedents.length > 0 && 
                          quoteConnections.pillars.length > 0
                        }
                      >
                        {(quoteConnections.tactics.length === 0 || 
                          quoteConnections.precedents.length === 0 || 
                          quoteConnections.pillars.length === 0) 
                           ? 'Fix Missing Connections' 
                           : 'All Connections Present'}
                      </button>
                      
                      <a
                        href={`/quote/${quoteConnections.quoteId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1.5 bg-blue-600 text-white rounded font-medium hover:bg-blue-700"
                      >
                        View Quote
                      </a>
                    </div>
                    
                    {/* View connection details */}
                    <div className="text-sm mb-3">
                      <h5 className="font-medium mb-1">Connection Details</h5>
                      {/* Tabs could go here, but to save time let's just show all */}
                      <div className="bg-gray-50 p-3 rounded">
                        <details className="mb-2">
                          <summary className="font-medium cursor-pointer">Authoritarian Tactics</summary>
                          {quoteConnections.tactics.length > 0 ? (
                            <div className="pl-4 pt-2">
                              {quoteConnections.tactics.map((tactic, index) => (
                                <div key={`tactic-${index}`} className="mb-2 pb-2 border-b">
                                  <div><span className="font-medium">Step {tactic.playbook_step_number}:</span> {tactic.name}</div>
                                  <div className="text-xs text-gray-600 mt-1">{tactic.justification}</div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="pl-4 pt-2 text-gray-600">No tactics connected</div>
                          )}
                        </details>
                        
                        <details className="mb-2">
                          <summary className="font-medium cursor-pointer">Historical Precedents</summary>
                          {quoteConnections.precedents.length > 0 ? (
                            <div className="pl-4 pt-2">
                              {quoteConnections.precedents.map((precedent, index) => (
                                <div key={`precedent-${index}`} className="mb-2 pb-2 border-b">
                                  <div><span className="font-medium">{precedent.country}</span> ({precedent.time_period})</div>
                                  <div className="text-xs text-gray-600 mt-1">{precedent.relevance_note}</div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="pl-4 pt-2 text-gray-600">No precedents connected</div>
                          )}
                        </details>
                        
                        <details className="mb-2">
                          <summary className="font-medium cursor-pointer">Democratic Pillars</summary>
                          {quoteConnections.pillars.length > 0 ? (
                            <div className="pl-4 pt-2">
                              {quoteConnections.pillars.map((pillar, index) => (
                                <div key={`pillar-${index}`} className="mb-2 pb-2 border-b">
                                  <div><span className="font-medium">{pillar.name}</span> (Impact: {pillar.impact_level})</div>
                                  <div className="text-xs text-gray-600 mt-1">{pillar.justification}</div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="pl-4 pt-2 text-gray-600">No pillars connected</div>
                          )}
                        </details>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-red-50 border border-red-200 p-3 rounded">
                    <p className="text-red-700">Quote #{quoteConnections.quoteId} not found</p>
                  </div>
                )}
              </div>
            )}
            
            {!connectionStats && !quoteConnections && (
              <div className="bg-blue-50 p-3 rounded border border-blue-200">
                <p className="text-blue-800">
                  Click "Check All Quotes" to see connection statistics, or enter a specific Quote ID and click "Check Quote" to view connections for that quote.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Article Schema Manager */}
      <div className="mb-10">
        <h2 className="text-xl font-bold mb-4">Article Schema Manager</h2>
        <ArticleSchemaManager />
      </div>
      
      <DbSchemaViewer />
      
      <div className="mt-8 bg-blue-50 p-4 rounded-md border border-blue-200">
        <h2 className="text-lg font-semibold text-blue-800 mb-2">Database Schema Inspection Tool</h2>
        <p className="text-sm text-blue-700 mb-2">
          This page helps diagnose database issues by showing you all tables and their structures in your D1 database.
        </p>
        <h3 className="text-md font-medium text-blue-800 mt-4 mb-2">Required Tables</h3>
        <p className="text-sm text-blue-700 mb-2">
          For your Quotes app to function properly, you should have these tables:
        </p>
        <ul className="list-disc pl-5 text-sm text-blue-700 space-y-1">
          <li><code className="bg-blue-100 px-1 rounded">quotes</code> - Stores the quotes themselves</li>
          <li><code className="bg-blue-100 px-1 rounded">tags</code> - Stores tag names</li>
          <li><code className="bg-blue-100 px-1 rounded">quote_tags</code> - Junction table linking quotes to tags</li>
        </ul>
        
        <h3 className="text-md font-medium text-blue-800 mt-4 mb-2">Optional Framework Tables</h3>
        <p className="text-sm text-blue-700 mb-2">
          For displaying additional democracy framework information:
        </p>
        <ul className="list-disc pl-5 text-sm text-blue-700 space-y-1">
          <li><code className="bg-blue-100 px-1 rounded">democratic_pillars</code> - Democracy pillar definitions</li>
          <li><code className="bg-blue-100 px-1 rounded">authoritarian_tactics</code> - Authoritarian tactic definitions</li>
          <li><code className="bg-blue-100 px-1 rounded">historical_precedents</code> - Historical precedent examples</li>
          <li><code className="bg-blue-100 px-1 rounded">quote_pillar_impacts</code> - Links quotes to democracy pillars</li>
          <li><code className="bg-blue-100 px-1 rounded">quote_tactic_connections</code> - Links quotes to authoritarian tactics</li>
          <li><code className="bg-blue-100 px-1 rounded">quote_precedent_connections</code> - Links quotes to historical precedents</li>
        </ul>
        
        <h3 className="text-md font-medium text-blue-800 mt-4 mb-2">Democracy Indices Tables</h3>
        <p className="text-sm text-blue-700 mb-2">
          For displaying democracy indices data:
        </p>
        <ul className="list-disc pl-5 text-sm text-blue-700 space-y-1">
          <li><code className="bg-blue-100 px-1 rounded">democracy_indices_enhanced</code> - Stores all democracy index data</li>
          <li><code className="bg-blue-100 px-1 rounded">democracy_index_sources</code> - Information about data sources</li>
          <li><code className="bg-blue-100 px-1 rounded">democracy_index_types</code> - Different types of indices from each source</li>
          <li><code className="bg-blue-100 px-1 rounded">countries</code> - Country codes and names</li>
          <li><code className="bg-blue-100 px-1 rounded">quote_index_relevance</code> - Links quotes to specific indices</li>
        </ul>
      </div>
    </div>
  );
};

export default DbSchema;